import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { ScenarioEcoExt } from '@/classes/scenario/eco/ScenarioEcoExt';
import { axiosWrapper } from '@/services/axiosWrapper';
import { StepConfig } from '@/classes/StepConfig';
import { GridPriceResume } from '@/classes/GridPriceResume';
import { BackendGridPriceResume } from '@/interfaces/BackendGridPriceResume';

type AxiosWrapper = Pick<typeof axiosWrapper, 'postToApi'>

interface Dependencies {
  axiosWrapper: AxiosWrapper
}

export const buildEcoModule = ({ axiosWrapper }: Dependencies) => {
  // This function should be inside EcoModule but vuex-module-decorators does not allow private methods...
  function isResumeDisplayable(updatedSteps: StepConfig[]): boolean {
    const relevantSteps = updatedSteps
      .filter(({ key }) => key === 'load' || key === 'grid');
    if (relevantSteps.length !== 2) {
      return false;
    }
    return relevantSteps
      .map(({ hasErrors, mustBeControlled }) => !hasErrors && mustBeControlled)
      .every(isValid => isValid);
  }

  @Module({
    namespaced: true
  })
  class EcoModule extends VuexModule {
    private gridPriceResume: GridPriceResume | null = null;

    @Action
    public async reloadGridPriceResume(): Promise<typeof EcoModule.prototype.gridPriceResume> {
      const updatedSteps: StepConfig[] = this.context.rootGetters['stepper/getUpdatedSteps'];
      if (!isResumeDisplayable(updatedSteps)) {
        this.context.commit('refreshGridPriceResume', null);
        return Promise.resolve(null);
      }
      const { load, eco, grid } = this.getConfig;
      const ecoDto = eco?.toDto();
      const gridDto = grid?.toDto();
      try {
        const { data } = await axiosWrapper.postToApi<BackendGridPriceResume>('/eco/pricingSummary', {
          load: load?.calculatedCurveBefore?.values,
          eco: ecoDto,
          grid: gridDto
        }, 'complexPricingSummary');
        const cleanedData = new GridPriceResume(data);
        this.context.commit('refreshGridPriceResume', cleanedData);
        return cleanedData;
      } catch (error) {
        this.context.commit('refreshGridPriceResume', null);
        return Promise.resolve(null);
      }
    }

    @Mutation
    public refreshGridPriceResume(gridPriceResume: typeof EcoModule.prototype.gridPriceResume): void {
      this.gridPriceResume = gridPriceResume;
    }

    get getConfig(): ScenarioExt {
      return new ScenarioExt(this.context.rootGetters['scenario/getFields']);
    }

    get getFields(): ScenarioEcoExt {
      return new ScenarioEcoExt(this.getConfig.eco ?? {});
    }

    get getGridPriceResume(): typeof EcoModule.prototype.gridPriceResume {
      return this.gridPriceResume;
    }
  }

  return EcoModule;
};

export default buildEcoModule({ axiosWrapper });
